import React, { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import { ORIGIN_TAG_API } from '~support-at-home/lib/serviceOrigin';

import useEditableTableRows, { EditableRow } from '~support-at-home/hooks/useEditableTableRows';

import { Button, ErrorResponse } from '~components';
import { TableView } from '~components/table-view';

import columnsFactory from '~support-at-home/forms/client-services/listSingle';

import { getAll as getSahCategories } from '~lookups/actions/sahCategories';
import { useGetSahServicesQuery } from '~lookups/actions/sahServices';
import { create, deleteOne, getAll, update } from '~support-at-home/actions/clientServices';

import { selectSahCategories } from '~lookups/selectors/sahCategories';
import { selectSahClientServices } from '~support-at-home/selectors/clientServices';

import { filterServiceRowFields } from '../helpers';

// Names the dialog for screen readers (aria-labelledby on the dialog points at the header).
const MODAL_HEADER_ID = 'client-services-modal-title';

interface ComponentProps {
  id: number | null;
  isOpen?: boolean;
  onClose: (confirm: boolean) => void;
}

const ClientServicesModal: React.FC<ComponentProps> = ({ id, isOpen = false, onClose }) => {
  const dispatch = useAppDispatch();
  // The slice's rows are only replaced on getAll.fulfilled, and this modal is never unmounted, so on
  // reopening for a different client they still hold the PREVIOUS client's services until the new
  // fetch resolves. Rows for any other client are dropped here, so neither the table, the per-row
  // locks nor the read-only note reflect the wrong client in that window. Same guard as BudgetsModal.
  const loadedServices = useAppSelector(selectSahClientServices);
  const clientServices = useMemo(
    () => loadedServices.filter((service) => service.client_id === id),
    [loadedServices, id],
  );
  const categories = useAppSelector(selectSahCategories);
  const { data: servicesData } = useGetSahServicesQuery();

  const columns = useMemo(() => columnsFactory(servicesData ?? [], categories), [servicesData, categories]);

  const persistedServices: EditableRow[] = useMemo(
    () => clientServices.map((service) => ({ ...service, persisted: true, backendId: service.id })),
    [clientServices],
  );

  const getDefaultRow = useCallback(
    (): EditableRow => ({
      id: crypto.randomUUID(),
      client_id: id,
      service_id: '',
      start_date: '',
      end_date: '',
    }),
    [id],
  );

  const load = useCallback(() => {
    dispatch(getAll({ client_id: id }));
  }, [dispatch, id]);

  useEffect(() => {
    if (isOpen && id !== null) {
      load();
    }
  }, [id, isOpen, load]);

  // The Category column is derived from the SaH categories lookup, which nothing on this tab loads.
  // Without this it only filled in when another screen (e.g. a claim) had loaded the lookup first.
  const hasCategories = categories.length > 0;
  useEffect(() => {
    if (isOpen && !hasCategories) {
      dispatch(getSahCategories({}));
    }
  }, [dispatch, isOpen, hasCategories]);

  const { rows, tableRef, error, isProcessing, handleChange, canRowValidate, confirmChanges } = useEditableTableRows({
    source: persistedServices,
    getDefaultRow,
    onCreate: async (row) => {
      const toCreate = filterServiceRowFields(row, columns, id);
      await dispatch(create(toCreate)).unwrap();
    },
    onUpdate: async (row) => {
      if (!row.backendId) return;
      const toUpdate = filterServiceRowFields(row, columns, id);
      await dispatch(update({ ...toUpdate, id: row.backendId })).unwrap();
    },
    onDelete: async (row) => {
      if (!row.backendId) return;
      await dispatch(deleteOne({ id: row.backendId })).unwrap();
    },
  });

  // Checked against the persisted rows, not the in-progress `rows`, since a newly-added row is never
  // 'api'.
  const hasSaService = clientServices.some((service) => service.origin_tag === ORIGIN_TAG_API);

  const handleConfirm = async () => {
    const outcome = await confirmChanges();
    if (outcome === 'saved') {
      load();
      onClose(true);
    } else if (outcome === 'clean') {
      onClose(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      size="xl"
      style={{ maxWidth: '1500px', width: '100%' }}
      labelledBy={MODAL_HEADER_ID}
      data-cy="client-services-modal"
    >
      <ModalHeader className="bg-success text-white" id={MODAL_HEADER_ID}>
        Allocated SAH Services
      </ModalHeader>
      <ModalBody>
        <ErrorResponse message={error} dataCy="client-services-modal-error" />
        {hasSaService && (
          <p className="text-muted small" data-cy="client-services-modal-readonly-note">
            Approved services sourced from Services Australia are updated by the import, so their values cannot be
            edited here. They can still be removed, but the next import will add them back while Services Australia
            still lists them.
          </p>
        )}
        <TableView
          ref={tableRef}
          id="client-services-modal"
          columns={columns}
          data={rows}
          editonly
          onChange={handleChange}
          canRowValidate={canRowValidate}
          canDeleteRow
          showAddRow
          emptyMessage="No data found"
          bordered
          compactView
        />
      </ModalBody>
      <ModalFooter className="justify-content-end">
        <Button
          size="sm"
          color="success"
          className="ps-4 pe-4 mx-2"
          onClick={handleConfirm}
          loading={isProcessing}
          dataCy="client-services-modal-confirm-button"
        >
          Confirm
        </Button>
        <Button
          size="sm"
          className="ps-4 pe-4 mx-2"
          onClick={() => onClose(false)}
          disabled={isProcessing}
          dataCy="client-services-modal-cancel-button"
        >
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ClientServicesModal;
